import React from "react"

import {
    Accordion,
    ActionIcon,
    Box,
    Button,
    Collapse,
    Group,
    Popover,
    Select,
    Stack,
    Switch,
    Text,
} from "@mantine/core"
import { IconInfoCircle, IconPlus, IconSettings } from "@tabler/icons-react"

import ModuleSettingsCard from "../module-settings-card/ModuleSettingsCard"
import PresetSelectItem from "./PresetSelectItem"
import DayOfWeekCheckbox from "./DayOfWeekCheckbox"
import OperatingTimeSettingLineControl from "./OperatingTimeSettingLineControl"
import OperatingTimeSettingLine from "./OperatingTimeSettingLine"
import useDeviceOperatingTime from "./useOperatingTimesModuleSettings"
import { ModuleSettingsProps } from "../module-settings-card/ModuleSettingsProps"
import { Attribute } from "../../../../contexts/devices-context"
import { WittyPiSchedule } from "../../hooks/useParseSchedule"
import { ModuleFC } from "../module-settings-card/ModuleFC"
import _DeviceOperatingTimeModuleSettings from "./_DeviceOperatingTimeModuleSettings"
import _GroupOperatingTimeModuleSettings from "./_GroupOperatingTimeModuleSettings"
import SettingsOverwriteWarning from "../settings-overwrite-warning/SettingsOverwriteWarning"
import { useLanguage } from "../../../../contexts/language-context"
import schedulePresets from "./schedulePresets"

export type WittyPiScheduleAttribute = Attribute & {
    value: string | { name: string; inputs: string[]; day_pattern?: WittyPiSchedule["dayPattern"] } | null
}

type OperatingTimeModuleSettingsProps = Omit<ModuleSettingsProps<string>, "attrs"> & {
    attrs: Record<"schedule", WittyPiScheduleAttribute> | undefined,
    coordinates?: {latitude: number, longitude: number}
}

const OperatingTimeModuleSettings: ModuleFC<OperatingTimeModuleSettingsProps> = Object.assign(
    ({ attrs, organisation, group, device, coordinates }) => {
        const {
            form,
            onSubmit,
            reset,
            inheritSettings,
            times,
            loading,
            dayPatternIsComplete,
            toggleDayPattern,
            addRule,
            removeRule,
            processing,
        } = useDeviceOperatingTime(attrs, organisation, group, device, coordinates)
        const { L } = useLanguage()

        return (
            <ModuleSettingsCard title={L("Time schedule")} icon={IconSettings}>
                <form onSubmit={form.onSubmit(onSubmit)}>
                    <Stack spacing="xl">
                        <SettingsOverwriteWarning attrs={attrs} group={group} device={device} />
                        <Select
                            data={[
                                { value: "custom", label: L("Custom settings"), group: L("Custom settings") },
                                ...schedulePresets(L),
                            ]}
                            description={
                                form.values.preset === "reboot_nightly"
                                    ? L(
                                          "This schedule should only be used when the device is powered with a cable instead of the battery.",
                                      )
                                    : form.values.preset === "wake_sunday_night" || form.values.preset === "wake_daily_noon"
                                    ? L(
                                          "This schedule will limit the connectivity of your device. Updates will only occur at the specified times.",
                                      )
                                    : null
                            }
                            inputWrapperOrder={["label", "input", "description", "error"]}
                            itemComponent={PresetSelectItem}
                            searchable
                            withinPortal
                            {...form.getInputProps("preset")}
                        />
                        <Collapse in={form.values.preset === "custom"}>
                            <Stack>
                                <Switch
                                    label={L("Execute every day")}
                                    description={L("Execute the time schedule regardless of the day.")}
                                    checked={dayPatternIsComplete}
                                    onChange={toggleDayPattern}
                                />
                                <Collapse ml="3.375rem" in={!dayPatternIsComplete}>
                                    <Group>
                                        <DayOfWeekCheckbox
                                            label={L("Mo")}
                                            checked={form.values.dayPattern?.[0]}
                                            {...form.getInputProps("dayPattern.0")}
                                        />
                                        <DayOfWeekCheckbox
                                            label={L("Tu")}
                                            checked={form.values.dayPattern?.[1]}
                                            {...form.getInputProps("dayPattern.1")}
                                        />
                                        <DayOfWeekCheckbox
                                            label={L("We")}
                                            checked={form.values.dayPattern?.[2]}
                                            {...form.getInputProps("dayPattern.2")}
                                        />
                                        <DayOfWeekCheckbox
                                            label={L("Th")}
                                            checked={form.values.dayPattern?.[3]}
                                            {...form.getInputProps("dayPattern.3")}
                                        />
                                        <DayOfWeekCheckbox
                                            label={L("Fr")}
                                            checked={form.values.dayPattern?.[4]}
                                            {...form.getInputProps("dayPattern.4")}
                                        />
                                        <DayOfWeekCheckbox
                                            label={L("Sa")}
                                            checked={form.values.dayPattern?.[5]}
                                            {...form.getInputProps("dayPattern.5")}
                                        />
                                        <DayOfWeekCheckbox
                                            label={L("Su")}
                                            checked={form.values.dayPattern?.[6]}
                                            {...form.getInputProps("dayPattern.6")}
                                        />
                                    </Group>
                                </Collapse>
                                <Group position="apart" align="start" noWrap>
                                    <Text size="sm">
                                        {L("Time schedule rules")}
                                        <Text size="xs" color="dimmed">
                                            {L("Determine rules when the device is active. Rules may overlap.")}
                                        </Text>
                                        <Text size="xs" color="red">
                                            {form.errors.lines}
                                        </Text>
                                    </Text>
                                    <Popover width={480} shadow="md" withinPortal>
                                        <Popover.Target>
                                            <ActionIcon>
                                                <IconInfoCircle size={24} />
                                            </ActionIcon>
                                        </Popover.Target>
                                        <Popover.Dropdown>
                                            <Stack spacing="xs">
                                                <Text weight="bold">{L("About custom time schedules")}</Text>
                                                <Text size="sm">
                                                    {L(
                                                        "Selecting custom time schedules allows more fine-grained control over your device. However, if not configured properly, custom schedules could potentially disable your devices, requiring on-site maintenance. For optimal use, please be aware of the following guidelines.",
                                                    )}
                                                </Text>
                                                <Box m={0} component="ul">
                                                    <Text size="sm" component="li">
                                                        {L(
                                                            "To ensure optimal use, your custom schedule might be slightly adjusted to ensure its correctness. This may include maintaining a minimal on-time per schedule event and skipping excessively short on/off events.",
                                                        )}
                                                    </Text>
                                                    <Text size="sm" component="li">
                                                        {L(
                                                            "If your device runs on battery, ensure your schedule doesn't activate it too frequently. Excessive activations can significantly reduce the device's battery life, requiring more frequent battery changes.",
                                                        )}
                                                    </Text>
                                                    <Text size="sm" component="li">
                                                        {L(
                                                            "Keep in mind that your device won't receive new configurations when powered off. Updates will only occur upon the next scheduled power-on.",
                                                        )}
                                                    </Text>
                                                </Box>
                                            </Stack>
                                        </Popover.Dropdown>
                                    </Popover>
                                </Group>
                                <Accordion
                                    variant="contained"
                                    chevronPosition="left"
                                    styles={{ label: { padding: "0.5rem 0" } }}
                                >
                                    {form.values.lines.map((line, index) => (
                                        <Accordion.Item value={index.toString()} key={index}>
                                            <OperatingTimeSettingLineControl line={line} onRemove={removeRule(index)} />
                                            <Accordion.Panel>
                                                <OperatingTimeSettingLine
                                                    times={times}
                                                    loading={loading}
                                                    coordinates={coordinates}
                                                    device={device}
                                                    {...form.getInputProps(`lines.${index}`)}
                                                />
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                                <Group position="right" mr="xs">
                                    <ActionIcon variant="default" onClick={addRule}>
                                        <IconPlus size={18} />
                                    </ActionIcon>
                                </Group>
                            </Stack>
                        </Collapse>
                        <Group position="right">
                            {device && (
                                <Button variant="subtle" onClick={inheritSettings} disabled={form.values.preset === null}>
                                    {L("Apply group settings")}
                                </Button>
                            )}
                            <Button variant="subtle" onClick={reset} disabled={!form.isDirty()}>
                                {L("Reset")}
                            </Button>
                            <Button type="submit" disabled={!form.isDirty()} loading={processing}>
                                {L("Save")}
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </ModuleSettingsCard>
        )
    },
    {
        Device: _DeviceOperatingTimeModuleSettings,
        Group: _GroupOperatingTimeModuleSettings
    }
)

export default OperatingTimeModuleSettings
